import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Password strength validation
  const checkPasswordStrength = (password) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&#]/.test(password)
    };
  };

  const passwordStrength = checkPasswordStrength(newPassword);
  const isPasswordValid = Object.values(passwordStrength).every(v => v);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!isPasswordValid) {
      setMessage({ type: 'error', text: 'Password does not meet the required strength criteria' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.updatePassword({
        currentPassword,
        newPassword
      });

      setMessage({ type: 'success', text: 'Password updated successfully! Logging you out...' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Log out user and redirect to login page
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to update password' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto'
      }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h1 style={{ 
              fontSize: '32px', 
              fontWeight: 700, 
              marginBottom: '8px',
              color: '#1a202c'
            }}>
              Profile
            </h1>
            <p style={{ color: '#718096', marginBottom: 0 }}>
              Manage your account settings
            </p>
          </div>

          {/* User Information Card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginBottom: '20px', color: '#667eea' }}>
            Account Information
          </h3>
          
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={user?.name}
              disabled
              style={{ cursor: 'not-allowed', opacity: 0.7 }}
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={user?.email}
              disabled
              style={{ cursor: 'not-allowed', opacity: 0.7 }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Role</label>
            <input
              type="text"
              value={user?.role}
              disabled
              style={{ cursor: 'not-allowed', opacity: 0.7, textTransform: 'capitalize' }}
            />
          </div>
        </div>

        {/* Change Password Card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ marginBottom: '20px', color: '#667eea' }}>
            Change Password
          </h3>

          {message.text && (
            <div style={{
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`,
              color: message.type === 'success' ? '#166534' : '#991b1b',
              fontSize: '14px'
            }}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleUpdatePassword}>
            <div className="form-group">
              <label>Current Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Enter current password"
                  style={{ paddingRight: '45px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#718096',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>New Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  required
                  placeholder="Enter new password"
                  style={{ 
                    paddingRight: '45px',
                    borderColor: newPassword && !isPasswordValid ? '#f56565' : undefined
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#718096',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showNewPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {(passwordFocused || newPassword) && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '13px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                    Password must contain:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <PasswordRequirement 
                      met={passwordStrength.minLength} 
                      text="At least 8 characters"
                    />
                    <PasswordRequirement 
                      met={passwordStrength.hasUppercase} 
                      text="One uppercase letter (A-Z)"
                    />
                    <PasswordRequirement 
                      met={passwordStrength.hasLowercase} 
                      text="One lowercase letter (a-z)"
                    />
                    <PasswordRequirement 
                      met={passwordStrength.hasNumber} 
                      text="One number (0-9)"
                    />
                    <PasswordRequirement 
                      met={passwordStrength.hasSpecialChar} 
                      text="One special character (@$!%*?&#)"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Confirm New Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                  style={{ paddingRight: '45px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#718096',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || (newPassword && !isPasswordValid)}
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 600,
                opacity: (loading || (newPassword && !isPasswordValid)) ? 0.7 : 1,
                cursor: (loading || (newPassword && !isPasswordValid)) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Password requirement indicator component
function PasswordRequirement({ met, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        fontSize: '16px',
        color: met ? '#48bb78' : '#cbd5e0',
        fontWeight: 'bold'
      }}>
        {met ? '✓' : '○'}
      </span>
      <span style={{ color: met ? '#2d3748' : '#718096' }}>
        {text}
      </span>
    </div>
  );
}

export default Profile;
